import { z } from 'zod';

export const SyncErrorCategoryEnum = z.enum([
  'NETWORK_OFFLINE',
  'TRANSIENT_REMOTE',
  'AUTH_REQUIRED',
  'PERMISSION_DENIED',
  'VALIDATION_ERROR',
  'REMOTE_IMMUTABLE_CONFLICT',
  'UNKNOWN',
]);

export type SyncErrorCategory = z.infer<typeof SyncErrorCategoryEnum>;

export interface SyncErrorDetails {
  category: SyncErrorCategory;
  message: string;
  isRetryable: boolean;
  originalError?: unknown;
}

/**
 * Classifies runtime Firestore / network errors into deterministic taxonomy categories.
 */
export function classifySyncError(error: unknown): SyncErrorDetails {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return {
      category: 'NETWORK_OFFLINE',
      message: 'Client device is offline.',
      isRetryable: true,
      originalError: error,
    };
  }

  const msg = error instanceof Error ? error.message : String(error);
  const errorCode =
    typeof (error as Record<string, unknown>)?.code === 'string'
      ? ((error as Record<string, unknown>).code as string).toLowerCase()
      : '';
  const combined = `${msg} ${errorCode}`.toLowerCase();
  const lower = combined;

  if (
    lower.includes('remote_immutable_conflict') ||
    lower.includes('conflicting immutable contents') ||
    lower.includes('immutable conflict')
  ) {
    return {
      category: 'REMOTE_IMMUTABLE_CONFLICT',
      message: msg,
      isRetryable: false,
      originalError: error,
    };
  }

  if (
    lower.includes('offline') ||
    lower.includes('network') ||
    lower.includes('failed to fetch') ||
    lower.includes('net::err')
  ) {
    return {
      category: 'NETWORK_OFFLINE',
      message: msg,
      isRetryable: true,
      originalError: error,
    };
  }

  if (
    lower.includes('permission-denied') ||
    lower.includes('permission_denied') ||
    lower.includes('insufficient permission') ||
    lower.includes('missing or insufficient permissions')
  ) {
    return {
      category: 'PERMISSION_DENIED',
      message: msg,
      isRetryable: false,
      originalError: error,
    };
  }

  if (
    lower.includes('unauthenticated') ||
    lower.includes('not authenticated') ||
    lower.includes('auth/user-token-expired') ||
    lower.includes('auth/invalid-user-token')
  ) {
    return {
      category: 'AUTH_REQUIRED',
      message: msg,
      isRetryable: false,
      originalError: error,
    };
  }

  if (
    lower.includes('invalid argument') ||
    lower.includes('invalid-argument') ||
    lower.includes('validation') ||
    lower.includes('document schema')
  ) {
    return {
      category: 'VALIDATION_ERROR',
      message: msg,
      isRetryable: false,
      originalError: error,
    };
  }

  if (
    lower.includes('unavailable') ||
    lower.includes('timeout') ||
    lower.includes('deadline-exceeded') ||
    lower.includes('resource-exhausted') ||
    lower.includes('503')
  ) {
    return {
      category: 'TRANSIENT_REMOTE',
      message: msg,
      isRetryable: true,
      originalError: error,
    };
  }

  return {
    category: 'UNKNOWN',
    message: msg,
    isRetryable: true,
    originalError: error,
  };
}
